import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useScopeParams } from "./useScopeParams";
import { alertsService } from "@/services/alertsService";
import type { InsightProps } from "@/components/Insights/insights";

export const useAlertMutations = () => {
  const { id } = useScopeParams();
  const queryClient = useQueryClient();

  const createAlertMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      return await alertsService.createAlert(id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts", id] });
      queryClient.invalidateQueries({ queryKey: ["insights", id] });
    },
  });

  const deleteAlertMutation = useMutation({
    mutationFn: async (alertId: string) => {
      await alertsService.deleteAlert(id, alertId);
      return { id: alertId, type: "alert" };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["alerts", id] });

      queryClient.setQueryData(
        ["insights", id],

        (oldData: InsightProps[] = []) => {
          if (!oldData) return oldData;
          return oldData.map((insight) => {
            if (insight.alert?.id === data.id) {
              return { ...insight, alert: null };
            }
            return insight;
          });
        },
      );
      queryClient.invalidateQueries({ queryKey: ["insights", id] });
    },
  });

  const updateAlertMutation = useMutation({
    mutationFn: async ({
      alertId,
      payload,
    }: {
      alertId: string;
      payload: Record<string, unknown>;
    }) => {
      return await alertsService.updateAlert(id, alertId, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts", id] });
      queryClient.invalidateQueries({ queryKey: ["insights", id] });
    },
  });

  return {
    createAlert: createAlertMutation.mutate,
    deleteAlert: deleteAlertMutation.mutate,
    updateAlert: updateAlertMutation.mutate,
    isCreating: createAlertMutation.isPending,
    isCreateError: createAlertMutation.isError,
    resetCreateAlert: createAlertMutation.reset,
    isDeleting: deleteAlertMutation.isPending,
    isUpdating: updateAlertMutation.isPending,
    isUpdateError: updateAlertMutation.isError,
    resetUpdateAlert: updateAlertMutation.reset,
  };
};
